/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import OrbLoader from "../../components/common/OrbLoader";
import HistoryIcon from "@mui/icons-material/History";
import ManageHistoryIcon from "@mui/icons-material/ManageHistory";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { selectSidebarCollapsed } from "../../store/slices/app/appSelector";
import { setToastMessage } from "../../store/slices/app/appSlice";
import ClientSelector from "../../components/purchase-orders/ClientSelector";
import OcItemsTable from "../../components/purchase-orders/OcItemsTable";
import OcChangeLogDialog from "../../components/purchase-orders/OcChangeLogDialog";
import {
  useGetOcClientsQuery,
  useGetOcIssuersQuery,
  useGetOcOrderByIdQuery,
  useCreateOcOrderMutation,
  useUpdateOcOrderMutation,
  useSeedOcIssuersMutation,
} from "../../store/apis/ocApi";
import { useUserRole } from "../../hooks/useUserRole";
import {
  CreateOcOrderRequest,
  OcClient,
  OcInternalMode,
  OcTotals,
} from "../../types/oc";
import {
  only2dec,
  formatEUR,
  apiToInternalMode,
  internalToApiMode,
  normalizeOcItems,
  setFeeLine,
  FEE_LINE_FLAG,
  totalsMedios,
  totalsCreatividades,
  totalsProveedoresDefault,
  totalsProveedoresIncluye,
} from "../../utils/oc/ocCalc";
import { generateOcPdf } from "../../utils/oc/PDFGenerator";
import { buildPdfArgsFromDetail } from "../../utils/oc/buildPdfArgs";
import PageBreadcrumbs from "../../components/layout/PageBreadcrumbs";
import PageContainer from "../../components/layout/PageContainer";
import { OC_CARD_SX } from "../../components/purchase-orders/ocStyles";

const todayISO = () => new Date().toISOString().slice(0, 10);

interface PartyForm {
  label: string;
  legalName: string;
  taxId: string;
  address: string;
  city: string;
  zip: string;
  country: string;
  phone: string;
  email: string;
  website: string;
  paymentTerms: string;
}

const emptyParty: PartyForm = {
  label: "",
  legalName: "",
  taxId: "",
  address: "",
  city: "",
  zip: "",
  country: "",
  phone: "",
  email: "",
  website: "",
  paymentTerms: "",
};

// El marcador de la linea de fee no se persiste (normalizeOcItems lo elimina antes de
// guardar). Al reabrir una OC se re-marca la linea que tiene la forma que produce
// setFeeLine (inversion 0 y fee > 0) para que "Aplicar fee de company" la reemplace en
// lugar de anadir una segunda linea de fee.
const restoreFeeLineFlag = (items: any[]): any[] =>
  items.map((it) =>
    only2dec(it.inversion) === 0 && only2dec(it.fee) > 0
      ? { ...it, [FEE_LINE_FLAG]: true }
      : it
  );

const MODES: OcInternalMode[] = [
  "oc-medios",
  "oc-creatividades",
  "oc-proveedores",
  "oc-proveedores-incluye",
];

const PurchaseOrders: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const collapsed = useSelector(selectSidebarCollapsed);

  // Con :id la pagina funciona como editor de una OC existente; sin el, como creador.
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);

  const { data: clientsResp, error: clientsError } = useGetOcClientsQuery();
  const { data: issuersResp, error: issuersError } = useGetOcIssuersQuery();
  const {
    data: orderResp,
    isFetching: isLoadingOrder,
    error: orderError,
  } = useGetOcOrderByIdQuery(id ?? "", { skip: !id });
  const order = orderResp?.data;

  const [createOcOrder, { isLoading: isSaving }] = useCreateOcOrderMutation();
  const [updateOcOrder, { isLoading: isUpdating }] = useUpdateOcOrderMutation();
  const [changeLogOpen, setChangeLogOpen] = useState(false);

  // Id de la OC ya precargada: evita repisar el formulario en cada refetch (a partir de
  // la precarga el formulario es la fuente de verdad) y precarga de nuevo si cambia la OC.
  const prefilledForRef = useRef<string | null>(null);
  // El total de oc-proveedores es manual: al precargar no debe recalcularse.
  const provTotalPrefilledRef = useRef(false);

  // TODO(temporal): botón de setup de un solo uso para sembrar emisoras OC.
  // Eliminar (junto con el botón en el header) una vez ejecutado con éxito en producción.
  const { isAdmin, isStrategy } = useUserRole();
  const [seedOcIssuers, { isLoading: isSeeding }] = useSeedOcIssuersMutation();
  const handleSeedIssuers = async () => {
    try {
      const resp = await seedOcIssuers().unwrap();
      dispatch(
        setToastMessage({
          message:
            resp.message ||
            t("oc.toasts.issuersSynced", { count: resp.data?.length ?? 0 }),
          severity: "success",
        })
      );
    } catch (e: any) {
      dispatch(
        setToastMessage({
          message: e?.data?.message || t("oc.toasts.issuersSyncError"),
          severity: "error",
        })
      );
    }
  };

  const clients = useMemo(() => clientsResp?.data ?? [], [clientsResp]);
  const issuers = useMemo(() => issuersResp?.data ?? [], [issuersResp]);

  // ---- Metadatos OC ----
  const [ocDate, setOcDate] = useState(todayISO());
  const [campaign, setCampaign] = useState("");

  // ---- Emisora (issuer) ----
  // Sin emisora predeterminada a propósito: hay que elegirla, para no acabar emitiendo
  // desde la primera de la lista sin darse cuenta. handleSave avisa si falta, y al abrir una
  // OC existente se rellena con la que tenga guardada.
  const [issuerId, setIssuerId] = useState<string>("");
  const issuer = useMemo(
    () => issuers.find((i) => i.id === issuerId),
    [issuers, issuerId]
  );

  // ---- Cliente / proveedor ----
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  // Razon social elegida dentro de esa empresa. La OC la guarda, asi que al reabrirla el
  // selector marca la misma sin tener que deducirla de los datos fiscales congelados.
  const [selectedBillingId, setSelectedBillingId] = useState<string | null>(
    null
  );
  const [isNewClient, setIsNewClient] = useState(false);
  const [client, setClient] = useState<PartyForm>({ ...emptyParty });

  // Lo que se elige es una razon social (billing) de una empresa: la OC se liga a la
  // empresa (companyId) y los datos fiscales del formulario salen de esa razon social.
  const pickClient = (c: OcClient) => {
    setSelectedClientId(c.companyId);
    setSelectedBillingId(c.billingId || null);
    setIsNewClient(false);
    // Prefill del fee fijo con el de la company (editable). El variable se lee
    // en vivo desde el cliente seleccionado (ver selectedClient).
    setFeeFixedInput(only2dec(c.feeFixed));
    setClient({
      label: c.companyLabel,
      legalName: c.fiscalName,
      taxId: c.taxId,
      address: c.address,
      city: c.city,
      zip: c.zip,
      country: c.country,
      phone: c.phone,
      email: c.email,
      website: c.website,
      paymentTerms: c.paymentTerms,
    });
  };
  const pickNewClient = () => {
    setSelectedClientId(null);
    setSelectedBillingId(null);
    setIsNewClient(true);
    setClient({ ...emptyParty });
  };

  // Texto del selector: "razon social · empresa", como en el selector de la factura.
  const clientDisplayLabel = useMemo(() => {
    if (!client.legalName) return client.label;
    if (!client.label || client.label === client.legalName)
      return client.legalName;
    return `${client.legalName} · ${client.label}`;
  }, [client.label, client.legalName]);

  const [showCompanyContact, setShowCompanyContact] = useState(false);
  const [showClientContact, setShowClientContact] = useState(false);

  // ---- Items por modo ----
  const [itemsMedios, setItemsMedios] = useState<any[]>([
    { concepto: "", inversion: "", fee: "" },
  ]);
  const [itemsCreatividades, setItemsCreatividades] = useState<any[]>([
    { concepto: "", cantidad: "", precio: "" },
  ]);
  const [itemsProv, setItemsProv] = useState<any[]>([
    { concepto: "", inv: "", KPI: "", costKPI: "", result: "" },
  ]);
  const [itemsProvIncluye, setItemsProvIncluye] = useState<any[]>([
    {
      concepto: "",
      incluye: "",
      coste: "",
      useDateRange: false,
      fechaText: "",
      fechaInicio: ocDate,
      fechaFin: "",
    },
  ]);

  const [mode, setMode] = useState<OcInternalMode>("oc-medios");

  // Modo compacto automático con ≥12 filas
  const compactMode = useMemo(() => {
    if (mode === "oc-medios") return itemsMedios.length >= 12;
    if (mode === "oc-creatividades") return itemsCreatividades.length >= 12;
    if (mode === "oc-proveedores") return itemsProv.length >= 12;
    if (mode === "oc-proveedores-incluye") return itemsProvIncluye.length >= 12;
    return false;
  }, [
    mode,
    itemsMedios.length,
    itemsCreatividades.length,
    itemsProv.length,
    itemsProvIncluye.length,
  ]);

  // Totales
  const totMedios = useMemo(() => totalsMedios(itemsMedios), [itemsMedios]);
  const totCreatividades = useMemo(
    () => totalsCreatividades(itemsCreatividades),
    [itemsCreatividades]
  );
  const totProvIncluye = useMemo(
    () => totalsProveedoresIncluye(itemsProvIncluye),
    [itemsProvIncluye]
  );
  const [manualTotProv, setManualTotProv] = useState<number | string>(0);
  useEffect(() => {
    // Al abrir una OC existente manda el total guardado, no la suma de inversiones.
    if (provTotalPrefilledRef.current) {
      provTotalPrefilledRef.current = false;
      return;
    }
    setManualTotProv(totalsProveedoresDefault(itemsProv));
  }, [itemsProv]);

  // ---- Fee de company (solo modo oc-medios) ----
  const [feeType, setFeeType] = useState<"variable" | "fijo">("variable");
  const [feeFixedInput, setFeeFixedInput] = useState<number | string>(0);
  // Los fees son de la empresa, no de la razon social: vale cualquier opcion suya.
  const selectedClient = useMemo(
    () => clients.find((c) => c.companyId === selectedClientId),
    [clients, selectedClientId]
  );
  const companyFeeVariable = selectedClient?.feeVariable ?? 0;
  const companyFeeFixed = selectedClient?.feeFixed ?? 0;

  // El fee fijo sale de la ficha de la empresa, igual que el variable, pero el importe se
  // deja editable para poder ajustarlo en una OC concreta (y para las OCs de cliente ad-hoc,
  // que no tienen empresa del catálogo). Al cambiar de cliente se recarga el suyo y el toggle
  // arranca en el tipo de fee que esa empresa tiene configurado.
  useEffect(() => {
    if (!selectedClient) return;
    setFeeFixedInput(companyFeeFixed);
    if (companyFeeFixed > 0 && companyFeeVariable === 0) setFeeType("fijo");
    else if (companyFeeVariable > 0 && companyFeeFixed === 0)
      setFeeType("variable");
  }, [selectedClient, companyFeeFixed, companyFeeVariable]);

  // % variable "activo": cuando se aplica un fee variable, el porcentaje queda
  // fijado aquí para que la línea de fee se recalcule al cambiar la inversión.
  // null = no hay fee variable activo (fee fijo o sin aplicar).
  const [appliedFeePct, setAppliedFeePct] = useState<number | null>(null);

  const applyCompanyFee = () => {
    if (feeType === "variable") {
      setAppliedFeePct(companyFeeVariable);
      const feeTotal = only2dec((totMedios.inv * companyFeeVariable) / 100);
      setItemsMedios((prev) =>
        setFeeLine(
          prev,
          feeTotal,
          t("oc.feeLine.variable", { pct: companyFeeVariable })
        )
      );
    } else {
      setAppliedFeePct(null); // el fee fijo es estático, no sigue a la inversión
      setItemsMedios((prev) =>
        setFeeLine(prev, only2dec(feeFixedInput), t("oc.feeLine.fixed"))
      );
    }
  };

  // Mantén la línea de fee variable sincronizada con la inversión de medios.
  // Depende solo de totMedios.inv (que excluye la línea de fee, inv 0), así que
  // actualizar el fee no cambia la dependencia y no hay bucle.
  useEffect(() => {
    if (appliedFeePct == null) return;
    const feeTotal = only2dec((totMedios.inv * appliedFeePct) / 100);
    setItemsMedios((prev) => {
      const idx = prev.findIndex(
        (it) => (it as Record<string, unknown>)[FEE_LINE_FLAG]
      );
      if (idx === -1) return prev; // línea de fee borrada: nada que sincronizar
      if (only2dec(prev[idx].fee) === feeTotal) return prev; // ya está al día
      const next = [...prev];
      next[idx] = { ...next[idx], fee: feeTotal };
      return next;
    });
  }, [totMedios.inv, appliedFeePct]);

  // ---- Precarga del formulario en modo edicion ----
  useEffect(() => {
    if (!order || prefilledForRef.current === order.id) return;
    prefilledForRef.current = order.id;

    setOcDate(order.ocDate);
    setCampaign(order.campaign);
    setIssuerId(order.issuerCompanyId);
    setSelectedClientId(order.clientId);
    // Una OC emitida antes de guardarse la razon social (o servida por un backend que aun
    // no la devuelve) llega sin el campo: null, no undefined.
    setSelectedBillingId(order.clientBillingId ?? null);
    setIsNewClient(!order.clientId);
    const snapshot = order.clientSnapshot;
    setClient({
      label: snapshot.label,
      legalName: snapshot.legalName,
      taxId: snapshot.taxId,
      address: snapshot.address,
      city: snapshot.city,
      zip: snapshot.zip,
      country: snapshot.country,
      phone: snapshot.phone,
      email: snapshot.email,
      website: snapshot.website,
      paymentTerms: snapshot.paymentTerms,
    });
    setShowCompanyContact(order.showCompanyContact);
    setShowClientContact(order.showClientContact);

    const internalMode = apiToInternalMode(order.mode);
    setMode(internalMode);
    const loadedItems = order.items as any[];
    if (internalMode === "oc-medios") {
      setItemsMedios(restoreFeeLineFlag(loadedItems));
    } else if (internalMode === "oc-creatividades") {
      setItemsCreatividades(loadedItems);
    } else if (internalMode === "oc-proveedores") {
      provTotalPrefilledRef.current = true;
      setItemsProv(loadedItems);
      setManualTotProv(typeof order.totals === "number" ? order.totals : 0);
    } else {
      setItemsProvIncluye(loadedItems);
    }
  }, [order]);

  // ---- Errores de carga ----
  useEffect(() => {
    if (orderError) {
      dispatch(
        setToastMessage({
          message: t("oc.toasts.orderLoadError"),
          severity: "error",
        })
      );
      navigate("/purchase-orders/history");
    }
  }, [orderError, dispatch, navigate, t]);

  useEffect(() => {
    if (clientsError || issuersError) {
      dispatch(
        setToastMessage({
          message: t("oc.toasts.loadError"),
          severity: "error",
        })
      );
    }
  }, [clientsError, issuersError, dispatch, t]);

  // ---- Update / add / delete por modo ----
  const updaters: Record<
    OcInternalMode,
    {
      update: (i: number, p: any) => void;
      add: () => void;
      del: (i: number) => void;
      items: any[];
    }
  > = {
    "oc-medios": {
      items: itemsMedios,
      update: (i, p) =>
        setItemsMedios((prev) =>
          prev.map((it, idx) => (idx === i ? { ...it, ...p } : it))
        ),
      add: () =>
        setItemsMedios((prev) => [
          ...prev,
          { concepto: "", inversion: "", fee: "" },
        ]),
      del: (i) => setItemsMedios((prev) => prev.filter((_, idx) => idx !== i)),
    },
    "oc-creatividades": {
      items: itemsCreatividades,
      update: (i, p) =>
        setItemsCreatividades((prev) =>
          prev.map((it, idx) => (idx === i ? { ...it, ...p } : it))
        ),
      add: () =>
        setItemsCreatividades((prev) => [
          ...prev,
          { concepto: "", cantidad: "", precio: "" },
        ]),
      del: (i) =>
        setItemsCreatividades((prev) => prev.filter((_, idx) => idx !== i)),
    },
    "oc-proveedores": {
      items: itemsProv,
      update: (i, p) =>
        setItemsProv((prev) =>
          prev.map((it, idx) => (idx === i ? { ...it, ...p } : it))
        ),
      add: () =>
        setItemsProv((prev) => [
          ...prev,
          { concepto: "", inv: "", KPI: "", costKPI: "", result: "" },
        ]),
      del: (i) => setItemsProv((prev) => prev.filter((_, idx) => idx !== i)),
    },
    "oc-proveedores-incluye": {
      items: itemsProvIncluye,
      update: (i, p) =>
        setItemsProvIncluye((prev) =>
          prev.map((it, idx) => (idx === i ? { ...it, ...p } : it))
        ),
      add: () =>
        setItemsProvIncluye((prev) => [
          ...prev,
          {
            concepto: "",
            incluye: "",
            coste: "",
            useDateRange: false,
            fechaText: "",
            fechaInicio: ocDate,
            fechaFin: "",
          },
        ]),
      del: (i) =>
        setItemsProvIncluye((prev) => prev.filter((_, idx) => idx !== i)),
    },
  };
  const current = updaters[mode];

  // Totals payload por modo
  const buildTotals = (): OcTotals => {
    if (mode === "oc-medios") return totMedios;
    if (mode === "oc-creatividades") return totCreatividades;
    if (mode === "oc-proveedores") return only2dec(manualTotProv);
    return totProvIncluye;
  };

  const isProvider =
    mode === "oc-proveedores" || mode === "oc-proveedores-incluye";

  const handleSave = async () => {
    if (!issuerId) {
      dispatch(
        setToastMessage({
          message: t("oc.toasts.selectIssuer"),
          severity: "error",
        })
      );
      return;
    }
    const label = client.label || client.legalName;
    if (!label.trim()) {
      dispatch(
        setToastMessage({
          message: t("oc.toasts.clientNeedsName"),
          severity: "error",
        })
      );
      return;
    }

    const body: CreateOcOrderRequest = {
      mode: internalToApiMode(mode),
      ocDate,
      campaign,
      issuerCompanyId: issuerId,
      // clientId (y su razon social) only when picked from the catalog (not ad-hoc)
      ...(selectedClientId && !isNewClient
        ? {
            clientId: selectedClientId,
            ...(selectedBillingId
              ? { clientBillingId: selectedBillingId }
              : {}),
          }
        : {}),
      client: {
        label,
        legalName: client.legalName || undefined,
        taxId: client.taxId || undefined,
        address: client.address || undefined,
        city: client.city || undefined,
        zip: client.zip || undefined,
        country: client.country || undefined,
        phone: client.phone || undefined,
        email: client.email || undefined,
        website: client.website || undefined,
        paymentTerms: client.paymentTerms || undefined,
      },
      items: normalizeOcItems(mode, current.items),
      totals: buildTotals(),
      showCompanyContact,
      showClientContact,
      compactMode,
    };

    try {
      // Al editar, el backend conserva el nº de OC y registra el diff en el histórico.
      const resp =
        isEditMode && id
          ? await updateOcOrder({ id, body }).unwrap()
          : await createOcOrder(body).unwrap();
      const saved = resp.data;
      // Regenera el PDF en cliente desde el detalle devuelto (con snapshots + isRBA).
      await generateOcPdf(buildPdfArgsFromDetail(saved));
      dispatch(
        setToastMessage({
          message: isEditMode
            ? t("oc.toasts.orderUpdated", { number: saved.ocNumber })
            : t("oc.toasts.orderSaved", { number: saved.ocNumber }),
          severity: "success",
        })
      );
    } catch (e: any) {
      const msg =
        e?.data?.message ||
        (isEditMode ? t("oc.toasts.updateError") : t("oc.toasts.saveError"));
      dispatch(setToastMessage({ message: msg, severity: "error" }));
    }
  };

  const partyTitle = isProvider ? t("oc.provider") : t("oc.client");

  const formatWhen = (iso?: string) =>
    iso
      ? new Date(iso).toLocaleString(undefined, {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "";

  // El formulario no se pinta hasta tener la OC: evita guardar sobre valores vacíos.
  if (isEditMode && isLoadingOrder && !order) {
    return (
      <Box sx={{ display: "flex" }}>
        <Box
          sx={{
            flexGrow: 1,
            p: 3,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
            transition: "margin-left 0.3s ease",
            marginLeft: collapsed ? "64px" : "250px",
          }}
        >
          <OrbLoader
            size="large"
            label={t("common.loading", { defaultValue: "Cargando…" })}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex" }}>
      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          transition: "margin-left 0.3s ease",
          marginLeft: collapsed ? "64px" : "250px",
        }}
      >
        <PageBreadcrumbs />

        <PageContainer>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 2, sm: 0 },
              mb: 3,
            }}
          >
            <Box>
              <Typography variant="h3">
                {isEditMode ? t("oc.editTitle") : t("oc.title")}
              </Typography>
              <Typography
                variant="pSmall"
                sx={{ mt: 0.5, display: "block" }}
                color={theme.palette.neutral["600"]}
              >
                {isEditMode
                  ? t("oc.editSubtitle", { number: order?.ocNumber ?? "" })
                  : t("oc.subtitle")}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {isEditMode && (
                <Button
                  variant="outlined"
                  startIcon={<ManageHistoryIcon />}
                  onClick={() => setChangeLogOpen(true)}
                >
                  {t("oc.changelog.button")}
                </Button>
              )}
              {/* TODO(temporal): setup de un solo uso. Eliminar tras ejecutar en producción. */}
              {(isAdmin || isStrategy) && (
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleSeedIssuers}
                  disabled={isSeeding}
                >
                  {isSeeding ? t("oc.syncing") : t("oc.syncIssuers")}
                </Button>
              )}
              <Button
                variant="outlined"
                startIcon={<HistoryIcon />}
                onClick={() => navigate("/purchase-orders/history")}
              >
                {t("oc.historyButton")}
              </Button>
            </Box>
          </Box>

          {/* Rastro de auditoría de la OC que se está editando */}
          {isEditMode && order && (
            <Alert
              severity={order.invoiceCount ? "warning" : "info"}
              sx={{ mb: 3 }}
            >
              <Typography variant="pSmall" sx={{ display: "block" }}>
                {t("oc.audit.createdBy", {
                  name: order.createdByName || "—",
                  when: formatWhen(order.createdAt),
                })}
              </Typography>
              <Typography variant="pSmall" sx={{ display: "block" }}>
                {order.updatedByName
                  ? t("oc.audit.lastEdit", {
                      name: order.updatedByName,
                      when: formatWhen(order.updatedAt),
                    })
                  : t("oc.audit.neverEdited")}
              </Typography>
              {!!order.invoiceCount && (
                <Typography
                  variant="pSmall"
                  sx={{ display: "block", fontWeight: 600, mt: 0.5 }}
                >
                  {t("oc.audit.invoiceWarning", { count: order.invoiceCount })}
                </Typography>
              )}
            </Alert>
          )}

          {/* Metadatos */}
          <Paper elevation={0} sx={{ ...OC_CARD_SX, mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  label={t("oc.date")}
                  type="date"
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={ocDate}
                  onChange={(e) => setOcDate(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label={t("oc.number")}
                  fullWidth
                  size="small"
                  value={
                    isEditMode
                      ? (order?.ocNumber ?? "")
                      : t("oc.numberPlaceholder")
                  }
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label={t("oc.campaign")}
                  fullWidth
                  size="small"
                  value={campaign}
                  onChange={(e) => setCampaign(e.target.value)}
                />
              </Grid>
            </Grid>
          </Paper>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            {/* Emisora */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ ...OC_CARD_SX, height: "100%" }}>
                <Typography variant="pMedium" sx={{ fontWeight: 600, mb: 2 }}>
                  {t("oc.ourCompany")}
                </Typography>
                <TextField
                  select
                  label={t("oc.issuer")}
                  fullWidth
                  size="small"
                  value={issuerId}
                  onChange={(e) => setIssuerId(e.target.value)}
                  sx={{ mb: 2 }}
                >
                  {issuers.map((i) => (
                    <MenuItem key={i.id} value={i.id}>
                      {i.label}
                    </MenuItem>
                  ))}
                </TextField>
                {issuer && (
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label={t("oc.legalName")}
                        fullWidth
                        size="small"
                        value={issuer.legalName}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label={t("oc.taxId")}
                        fullWidth
                        size="small"
                        value={issuer.taxId}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label={t("oc.address")}
                        fullWidth
                        size="small"
                        value={issuer.address}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label={t("oc.city")}
                        fullWidth
                        size="small"
                        value={issuer.city}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label={t("oc.zip")}
                        fullWidth
                        size="small"
                        value={issuer.zip}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label={t("oc.country")}
                        fullWidth
                        size="small"
                        value={issuer.country}
                        disabled
                      />
                    </Grid>
                  </Grid>
                )}
                <FormControlLabel
                  sx={{ mt: 1 }}
                  control={
                    <Checkbox
                      checked={showCompanyContact}
                      onChange={(e) => setShowCompanyContact(e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="pSmall">
                      {t("oc.contactInfo")}
                    </Typography>
                  }
                />
              </Paper>
            </Grid>

            {/* Cliente / proveedor */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ ...OC_CARD_SX, height: "100%" }}>
                <Typography variant="pMedium" sx={{ fontWeight: 600, mb: 2 }}>
                  {partyTitle}
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <ClientSelector
                    clients={clients}
                    selectedId={selectedBillingId}
                    selectedLabel={clientDisplayLabel}
                    isNew={isNewClient}
                    onPick={pickClient}
                    onPickNew={pickNewClient}
                  />
                </Box>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t("oc.legalName")}
                      fullWidth
                      size="small"
                      value={client.legalName}
                      onChange={(e) =>
                        setClient((p) => ({ ...p, legalName: e.target.value }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t("oc.taxId")}
                      fullWidth
                      size="small"
                      value={client.taxId}
                      onChange={(e) =>
                        setClient((p) => ({ ...p, taxId: e.target.value }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t("oc.address")}
                      fullWidth
                      size="small"
                      value={client.address}
                      onChange={(e) =>
                        setClient((p) => ({ ...p, address: e.target.value }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t("oc.city")}
                      fullWidth
                      size="small"
                      value={client.city}
                      onChange={(e) =>
                        setClient((p) => ({ ...p, city: e.target.value }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t("oc.zip")}
                      fullWidth
                      size="small"
                      value={client.zip}
                      onChange={(e) =>
                        setClient((p) => ({ ...p, zip: e.target.value }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t("oc.country")}
                      fullWidth
                      size="small"
                      value={client.country}
                      onChange={(e) =>
                        setClient((p) => ({ ...p, country: e.target.value }))
                      }
                    />
                  </Grid>
                </Grid>
                <FormControlLabel
                  sx={{ mt: 1 }}
                  control={
                    <Checkbox
                      checked={showClientContact}
                      onChange={(e) => setShowClientContact(e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="pSmall">
                      {t("oc.contactInfo")}
                    </Typography>
                  }
                />
                {showClientContact && (
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label={t("oc.phone")}
                        fullWidth
                        size="small"
                        value={client.phone}
                        onChange={(e) =>
                          setClient((p) => ({ ...p, phone: e.target.value }))
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label={t("oc.email")}
                        fullWidth
                        size="small"
                        value={client.email}
                        onChange={(e) =>
                          setClient((p) => ({ ...p, email: e.target.value }))
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label={t("oc.web")}
                        fullWidth
                        size="small"
                        value={client.website}
                        onChange={(e) =>
                          setClient((p) => ({ ...p, website: e.target.value }))
                        }
                      />
                    </Grid>
                  </Grid>
                )}
                <Typography
                  variant="pSmallest"
                  color={theme.palette.neutral["600"]}
                  sx={{ mt: 1, display: "block" }}
                >
                  {t("oc.emptyFieldHint")}
                </Typography>
                <TextField
                  select
                  label={t("oc.paymentMethod")}
                  fullWidth
                  size="small"
                  sx={{ mt: 2 }}
                  value={client.paymentTerms || ""}
                  onChange={(e) =>
                    setClient((p) => ({ ...p, paymentTerms: e.target.value }))
                  }
                >
                  <MenuItem value="">{t("oc.selectPlaceholder")}</MenuItem>
                  <MenuItem value="30">{t("oc.days", { count: 30 })}</MenuItem>
                  <MenuItem value="45">{t("oc.days", { count: 45 })}</MenuItem>
                  <MenuItem value="60">{t("oc.days", { count: 60 })}</MenuItem>
                </TextField>
              </Paper>
            </Grid>
          </Grid>
        </PageContainer>

        {/* Modo + items */}
        <Paper elevation={0} sx={{ ...OC_CARD_SX, mb: 3 }}>
          <ToggleButtonGroup
            exclusive
            fullWidth
            value={mode}
            onChange={(_e, v) => {
              if (v) setMode(v as OcInternalMode);
            }}
            sx={{ mb: 2 }}
          >
            {MODES.map((m) => (
              <ToggleButton
                key={m}
                value={m}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                {t(`oc.builderModes.${m}`)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <OcItemsTable
            mode={mode}
            items={current.items}
            compactMode={compactMode}
            ocDate={ocDate}
            onUpdate={current.update}
            onAdd={current.add}
            onDelete={current.del}
          />

          {/* Totales */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                minWidth: 280,
                borderRadius: 3,
                bgcolor: theme.palette.neutral["100"],
              }}
            >
              {mode === "oc-medios" && (
                <>
                  <TotalRow
                    label={t("oc.totals.mediaInvestment")}
                    value={formatEUR(totMedios.inv)}
                  />
                  <TotalRow
                    label={t("oc.totals.fee")}
                    value={formatEUR(totMedios.fee)}
                  />
                  <TotalRow
                    label={t("oc.totals.total")}
                    value={formatEUR(totMedios.tot)}
                    bold
                  />

                  <Box
                    sx={{
                      mt: 1.5,
                      pt: 1.5,
                      borderTop: `1px solid ${theme.palette.neutral["300"]}`,
                    }}
                  >
                    <ToggleButtonGroup
                      exclusive
                      fullWidth
                      size="small"
                      value={feeType}
                      onChange={(_e, v) => {
                        if (v) setFeeType(v as "variable" | "fijo");
                      }}
                      sx={{ mb: 1.5 }}
                    >
                      <ToggleButton
                        value="variable"
                        sx={{ textTransform: "none" }}
                      >
                        {t("oc.totals.feeTypeVariable")}
                      </ToggleButton>
                      <ToggleButton value="fijo" sx={{ textTransform: "none" }}>
                        {t("oc.totals.feeTypeFixed")}
                      </ToggleButton>
                    </ToggleButtonGroup>

                    {feeType === "variable" ? (
                      <Typography
                        variant="pSmallest"
                        color={theme.palette.neutral["600"]}
                        sx={{ display: "block", mb: 1.5 }}
                      >
                        {selectedClient
                          ? t("oc.totals.companyFeePct", {
                              pct: companyFeeVariable,
                            })
                          : t("oc.totals.companyFeePctNoClient")}
                      </Typography>
                    ) : (
                      <>
                        <TextField
                          label={t("oc.totals.fixedFeeAmount")}
                          fullWidth
                          size="small"
                          inputProps={{
                            inputMode: "decimal",
                            style: { textAlign: "right" },
                          }}
                          value={feeFixedInput}
                          onChange={(e) =>
                            setFeeFixedInput(e.target.value.replace(",", "."))
                          }
                          sx={{ mb: 0.5 }}
                        />
                        <Typography
                          variant="pSmallest"
                          color={theme.palette.neutral["600"]}
                          sx={{ display: "block", mb: 1.5 }}
                        >
                          {selectedClient
                            ? t("oc.totals.companyFeeFixed", {
                                amount: formatEUR(companyFeeFixed),
                              })
                            : t("oc.totals.companyFeeFixedNoClient")}
                        </Typography>
                      </>
                    )}

                    <Button
                      variant="outlined"
                      fullWidth
                      size="small"
                      onClick={applyCompanyFee}
                      disabled={feeType === "variable" && !selectedClient}
                    >
                      {t("oc.totals.applyCompanyFee")}
                    </Button>
                  </Box>
                </>
              )}
              {mode === "oc-creatividades" && (
                <>
                  <TotalRow
                    label={t("oc.totals.totalQuantity")}
                    value={String(totCreatividades.cant)}
                  />
                  <TotalRow
                    label={t("oc.totals.total")}
                    value={formatEUR(totCreatividades.tot)}
                    bold
                  />
                </>
              )}
              {mode === "oc-proveedores" && (
                <TextField
                  label={t("oc.totals.total")}
                  fullWidth
                  size="small"
                  inputProps={{
                    style: { textAlign: "right", fontWeight: 600 },
                  }}
                  value={manualTotProv}
                  onChange={(e) =>
                    setManualTotProv(e.target.value.replace(",", "."))
                  }
                />
              )}
              {mode === "oc-proveedores-incluye" && (
                <TotalRow
                  label={t("oc.totals.total")}
                  value={formatEUR(totProvIncluye)}
                  bold
                />
              )}
            </Paper>
          </Box>
        </Paper>

        {/* Guardar */}
        <PageContainer>
          <Paper elevation={0} sx={OC_CARD_SX}>
            <Button
              variant="contained"
              fullWidth
              disabled={isSaving || isUpdating}
              onClick={handleSave}
              sx={{ py: 1.2 }}
            >
              {isSaving || isUpdating ? (
                <CircularProgress size={22} color="inherit" />
              ) : isEditMode ? (
                t("oc.updateButton")
              ) : (
                t("oc.saveButton")
              )}
            </Button>
          </Paper>
        </PageContainer>
      </Box>

      <OcChangeLogDialog
        open={changeLogOpen}
        ocId={id ?? null}
        ocNumber={order?.ocNumber}
        onClose={() => setChangeLogOpen(false)}
      />
    </Box>
  );
};

const TotalRow: React.FC<{ label: string; value: string; bold?: boolean }> = ({
  label,
  value,
  bold,
}) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.75 }}>
    <Typography variant="pSmall" sx={{ fontWeight: bold ? 700 : 400 }}>
      {label}
    </Typography>
    <Typography variant="pSmall" sx={{ fontWeight: bold ? 700 : 400 }}>
      {value}
    </Typography>
  </Box>
);

export default PurchaseOrders;
